
import { useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useAuth } from '@/lib/auth-context';
import { PageHeader } from '@/components/layout/PageHeader';
import { ArchiveTable } from '@/components/admin/ArchiveTable';
import { useArchive } from '@/hooks/useArchive';

export default function ArchivePage() {
  const navigate = useNavigate();
  const { user } = useAuth();

  // Admin-only page
  useEffect(() => {
    if (user && user.role !== 'admin') {
      navigate('/unauthorized', { replace: true });
    }
  }, [user, navigate]);

  const {
    prayers,
    isLoading,
    error,
    page,
    pageSize,
    totalItems,
    filters,
    setFilters,
    setPage,
    fetchPrayers,
    archivePrayer,
    bulkArchive,
    exportToCsv,
  } = useArchive();

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      {/* Header */}
      <PageHeader
        title="Prayer Archive"
        description="View, search, and manage all prayer requests"
      />

      {/* Error message */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center justify-between">
          <p className="text-red-800 dark:text-red-400">{error}</p>
          <button
            onClick={fetchPrayers}
            className="text-sm text-red-600 dark:text-red-400 hover:underline"
          >
            Try Again
          </button>
        </div>
      )}

      {/* Archive Table */}
      <ArchiveTable
        prayers={prayers}
        isLoading={isLoading}
        filters={filters}
        onFilterChange={setFilters}
        page={page}
        pageSize={pageSize}
        totalItems={totalItems}
        onPageChange={setPage}
        onArchive={archivePrayer}
        onBulkArchive={bulkArchive}
        onExport={exportToCsv}
      />

      {/* Help Section */}
      <div className="bg-slate-50 dark:bg-slate-800/50 rounded-lg p-6 space-y-4">
        <h3 className="font-medium text-slate-900 dark:text-white">
          Archive Tips
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-slate-600 dark:text-slate-400">
          <div className="flex items-start gap-2">
            <TipIcon className="w-5 h-5 text-primary-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-slate-900 dark:text-white">
                Bulk Archive
              </p>
              <p>
                Archive old prayers in bulk to keep your active list manageable.
                Only displayed and answered prayers can be bulk archived.
              </p>
            </div>
          </div>
          <div className="flex items-start gap-2">
            <TipIcon className="w-5 h-5 text-primary-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-slate-900 dark:text-white">
                Export Data
              </p>
              <p>
                Export prayers to CSV for record-keeping, reporting, or backup
                purposes. Filters apply to exports.
              </p>
            </div>
          </div>
          <div className="flex items-start gap-2">
            <TipIcon className="w-5 h-5 text-primary-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-slate-900 dark:text-white">
                Search & Filter
              </p>
              <p>
                Use the search bar to find specific prayers by content. Filter by
                status and date range for targeted results.
              </p>
            </div>
          </div>
          <div className="flex items-start gap-2">
            <TipIcon className="w-5 h-5 text-primary-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-slate-900 dark:text-white">
                Prayer Privacy
              </p>
              <p>
                Dismissed prayers are retained for record-keeping but are not
                visible in the active moderation or display queues.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function TipIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
      />
    </svg>
  );
}
